/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../../theme/theme"
import { css } from "@mui/material"
import { Box, Button, Typography, Alert } from "@mui/material"
import { useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate"

const ContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
`)

const HintStyle = withMyTheme((theme) => css`
    font-size: 0.9rem;
    color: ${theme.palette.text.primary};
    text-align: left;
    font-style: italic;
`)

const UploadAreaStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    border: 2px dashed ${theme.palette.primary.main};
    border-radius: 12px;
    background-color: ${theme.palette.grey[50]};
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
        background-color: ${theme.palette.grey[100]};
        border-color: ${theme.palette.primary.dark};
    }
`)

const UploadAreaDraggingStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    border: 2px dashed ${theme.palette.primary.dark};
    border-radius: 12px;
    background-color: ${theme.palette.primary.light};
    cursor: pointer;
    transition: all 0.3s;
`)

const ImagePreviewStyle = withMyTheme((theme) => css`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 200px;
    margin: 0 auto;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    
    img {
        width: 100%;
        max-height: 120px;
        object-fit: contain;
        display: block;
        background-color: ${theme.palette.grey[100]};
    }
`)

const ChangeButtonStyle = withMyTheme((theme) => css`
    margin: 0.25rem auto 0 auto;
    display: block;
    padding: 6px 16px;
    font-size: 0.875rem;
`)

interface MainImageUploadProps {
    hint: string
    image: string
    orientation: 'landscape' | 'portrait'
    minWidth: number
    minHeight: number
    onImageChange: (image: string) => void
}

export const MainImageUpload = ({
    hint,
    image,
    orientation,
    minWidth,
    minHeight,
    onImageChange
}: MainImageUploadProps) => {
    const { t } = useTranslation()
    const [error, setError] = useState<string>('')
    const [isDragging, setIsDragging] = useState<boolean>(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const validateImage = (img: HTMLImageElement): string | null => {
        const width = img.naturalWidth
        const height = img.naturalHeight
        
        // Check minimum resolution
        if (width < minWidth || height < minHeight) {
            return `${t('questionnaire.mainImages.validation.tooSmall')}${minWidth}x${minHeight}`
        }
        
        // Check aspect ratio
        const isLandscape = width > height
        const isPortrait = height > width
        
        if (orientation === 'landscape' && !isLandscape) {
            return t('questionnaire.mainImages.validation.desktopShouldBeLandscape')
        }
        
        if (orientation === 'portrait' && !isPortrait) {
            return t('questionnaire.mainImages.validation.mobileShouldBePortrait')
        }
        
        return null
    }

    const processFile = (file: File) => {
        if (!file.type.startsWith('image/')) {
            setError(t('questionnaire.mainImages.validation.notAnImage'))
            return
        }
        
        setError('')
        const reader = new FileReader()
        reader.onload = (e) => {
            const img = new Image()
            img.onload = () => {
                const validationError = validateImage(img)
                if (validationError) {
                    setError(validationError)
                } else {
                    onImageChange(e.target?.result as string)
                }
            }
            img.src = e.target?.result as string
        }
        reader.readAsDataURL(file)
    }

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            processFile(file)
        }
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleClick = () => {
        fileInputRef.current?.click()
    }

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
    }

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        const file = e.dataTransfer.files?.[0]
        if (file) {
            processFile(file)
        }
    }

    return (
        <Box css={ContainerStyle}>
            <Typography css={HintStyle}>{hint}</Typography>
            
            {error && (
                <Alert severity="error" onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />

            {image ? (
                <>
                    <Box css={ImagePreviewStyle}>
                        <img src={image} alt="Preview" />
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={handleClick}
                        css={ChangeButtonStyle}
                    >
                        {t('questionnaire.mainImages.changeButton')}
                    </Button>
                </>
            ) : (
                <Box 
                    css={isDragging ? UploadAreaDraggingStyle : UploadAreaStyle} 
                    onClick={handleClick}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <AddPhotoAlternateIcon sx={{ fontSize: 60 }} color="primary" />
                    <Typography variant="h6" color="primary">
                        {t('questionnaire.mainImages.uploadButton')}
                    </Typography>
                </Box>
            )}
        </Box>
    )
}
